/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { type CommonProps, type TooltipObjectProps } from '../../_common/types';
import { useSemicontrolledState } from '../../_common/use-semi-controlled-state';
import { needleWarningMessage } from '../../_common/utils';
import { Checkbox } from '../../checkbox';
import { CleanIconButton } from '../../clean-icon-button';
import { ConditionalWrap } from '../../conditional-wrap';
import { isApplePlatform } from '../../helpers/platform';
import { ChevronRightIconOutline } from '../../icons/generated/heroIcons/ChevronRightIconOutline';
import { EllipsisHorizontalIconOutline } from '../../icons/generated/heroIcons/EllipsisHorizontalIconOutline';
import { Menu } from '../../menu';
import { Skeleton } from '../../skeleton';
import { Tooltip } from '../../tooltip';
import {
  computeNextSelection,
  computeSelectionEdges,
  countChangedIds,
  formatSelectionAnnouncement,
  type SelectionAnchor,
  type SelectionEdge,
  type SelectionIntent,
  type SelectionMode,
} from './selection';

type ItemEntry = {
  isDisabled: boolean;
  isSelectable: boolean;
  isSelected: boolean;
  onSelectedChange?: (isSelected: boolean) => void;
  parentId: string | null;
  toggleExpanded: () => void;
};

type TreeViewContextValue = {
  isRootManaged: boolean;
  level: number;
  onSelectionIntent: (itemId: string, intent: SelectionIntent) => void;
  parentId: string | null;
  registerItem: (itemId: string, entry: ItemEntry | null) => void;
  selectionMode: SelectionMode;
  setFocusedId: (id: string | null) => void;
  store: TreeViewStore;
};

const noop = () => undefined;

const EMPTY_EDGES: ReadonlyMap<string, SelectionEdge> = new Map();
const EMPTY_IDS: ReadonlySet<string> = new Set();
const EMPTY_ID_LIST: string[] = [];

type TreeViewStoreState = {
  edgeById: ReadonlyMap<string, SelectionEdge>;
  focusedId: string | null;
  /** Mirror of the selected ids; only meaningful when the root manages selection. */
  selectedIds: ReadonlySet<string>;
};

type TreeViewStore = {
  getState: () => TreeViewStoreState;
  setState: (partial: Partial<TreeViewStoreState>) => void;
  subscribe: (listener: () => void) => () => void;
};

/**
 * Volatile per-interaction state (focus, root-managed selection, edge
 * styling) lives in this store instead of React context, so each item can
 * subscribe to its own primitive slice via `useSyncExternalStore` and an
 * interaction only re-renders the rows whose slice actually changed.
 */
function createTreeViewStore(
  initialState?: Partial<TreeViewStoreState>,
): TreeViewStore {
  let state: TreeViewStoreState = {
    edgeById: EMPTY_EDGES,
    focusedId: null,
    selectedIds: EMPTY_IDS,
    ...initialState,
  };
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    setState: (partial) => {
      state = { ...state, ...partial };
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const TREE_EVENTS = {
  action: 'treeitem-action',
  collapse: 'treeitem-collapse',
  expand: 'treeitem-expand',
} as const;

const TreeViewContext = createContext<TreeViewContextValue>({
  isRootManaged: false,
  level: 1,
  onSelectionIntent: noop,
  parentId: null,
  registerItem: noop,
  selectionMode: 'single',
  setFocusedId: noop,
  store: createTreeViewStore(),
});

/**
 * On Apple platforms Ctrl+click is the context menu gesture, so only Cmd may
 * stand for an additive selection there.
 */
function getSelectionIntent(
  event: React.KeyboardEvent | React.MouseEvent,
): SelectionIntent {
  return {
    isRange: event.shiftKey,
    isToggle: isApplePlatform() ? event.metaKey : event.ctrlKey,
  };
}

function getVisibleTreeItems(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>('[role="treeitem"]'),
  ).filter((el) => {
    let parent = el.parentElement;
    while (parent && parent !== root) {
      if (parent.getAttribute('role') === 'group' && parent.hidden) {
        return false;
      }
      parent = parent.parentElement;
    }
    return true;
  });
}

function getVisibleItemIds(root: HTMLElement): string[] {
  return getVisibleTreeItems(root).map((el) => el.id);
}

function areIdSetsEqual(
  first: ReadonlySet<string>,
  second: ReadonlySet<string>,
): boolean {
  if (first.size !== second.size) {
    return false;
  }
  for (const id of first) {
    if (!second.has(id)) {
      return false;
    }
  }
  return true;
}

function areEdgeMapsEqual(
  first: ReadonlyMap<string, SelectionEdge>,
  second: ReadonlyMap<string, SelectionEdge>,
): boolean {
  if (first.size !== second.size) {
    return false;
  }
  for (const [id, edge] of first) {
    if (second.get(id) !== edge) {
      return false;
    }
  }
  return true;
}

function annotateTreeItems(children: React.ReactNode): React.ReactNode {
  const items = Children.toArray(children);
  const treeItemCount = items.filter(
    (child): child is React.ReactElement =>
      isValidElement(child) && child.type === Item,
  ).length;

  let posInSet = 0;
  return Children.map(children, (child) => {
    if (isValidElement(child) && child.type === Item) {
      posInSet++;
      return cloneElement(
        child as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
        {
          'aria-posinset': posInSet,
          'aria-setsize': treeItemCount,
        },
      );
    }
    return child;
  });
}

type RootProps = {
  /** The aria-label for the tree. Required for accessibility, unless using ariaLabelledby. Pass null to omit. */
  ariaLabel: string | null;
  /** The aria-labelledby for the tree. Pass a string of space-separated IDs of elements that label the tree. */
  ariaLabelledby?: string;
  /** The children of the tree. Should be TreeView.Item components or TreeView.SkeletonItem components only. */
  children?: React.ReactNode;
  /** Initially selected item ids for an uncontrolled root-managed selection. */
  defaultSelectedIds?: string[];
  /** Selected item ids. Makes the tree manage selection itself, instead of each item managing its own. */
  selectedIds?: string[];
  /** Selection mode for the tree. Defaults to "single". */
  selectionMode?: SelectionMode;
  /** Callback called with the complete next selection whenever the tree manages selection. Fires once per interaction. */
  onSelectedIdsChange?: (selectedIds: string[]) => void;
};

/**
 * Root container for a tree view. Renders a `role="tree"` element with full
 * keyboard navigation (arrow keys, Home/End, Enter/Space) following the
 * WAI-ARIA TreeView pattern.
 *
 * Direct children should be `TreeView.Item` or `TreeView.SkeletonItem`.
 *
 * @example
 * ```tsx
 * <TreeView ariaLabel="Files">
 *   <TreeView.Item title="README.md" onSelectedChange={setSelected} isSelected={selected} />
 * </TreeView>
 * ```
 *
 * @alpha - this component is still in development and may change in the future. It might not have feature parity with the old tree view yet.
 */
const Root = ({
  children,
  defaultSelectedIds,
  selectedIds: selectedIdsProp,
  selectionMode = 'single',
  ariaLabel,
  ariaLabelledby,
  className,
  style,
  htmlAttributes,
  onSelectedIdsChange,
  ref,
  ...restProps
}: CommonProps<'div', RootProps>) => {
  const treeRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([treeRef, ref]);
  const [announcement, setAnnouncement] = useState('');
  // While focus is inside the tree the root drops out of the tab order so
  // that Shift+Tab moves focus out of the tree instead of landing on the
  // root, which would delegate focus straight back to the item (a trap).
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const anchorRef = useRef<SelectionAnchor>(null);
  const entriesRef = useRef(new Map<string, ItemEntry>());

  const isRootManaged =
    selectedIdsProp !== undefined || defaultSelectedIds !== undefined;

  const storeRef = useRef<TreeViewStore>(null);
  if (storeRef.current === null) {
    storeRef.current = createTreeViewStore({
      selectedIds: new Set(
        selectedIdsProp ?? defaultSelectedIds ?? EMPTY_ID_LIST,
      ),
    });
  }
  const store = storeRef.current;

  const setFocusedId = useCallback(
    (id: string | null) => {
      if (store.getState().focusedId !== id) {
        store.setState({ focusedId: id });
      }
    },
    [store],
  );

  const [selectedIdList, setSelectedIdList] = useSemicontrolledState<string[]>({
    isControlled: selectedIdsProp !== undefined,
    onChange: onSelectedIdsChange,
    state: selectedIdsProp ?? defaultSelectedIds ?? EMPTY_ID_LIST,
  });

  // useSemicontrolledState recreates its setter whenever the value changes;
  // reading it through a ref keeps onSelectionIntent (and with it the
  // memoized context value) stable across selection changes.
  const setSelectedIdListRef = useRef(setSelectedIdList);
  useLayoutEffect(() => {
    setSelectedIdListRef.current = setSelectedIdList;
  });

  const readSelectedIds = useCallback((): Set<string> => {
    if (isRootManaged) {
      return new Set(store.getState().selectedIds);
    }
    const selected = new Set<string>();
    entriesRef.current.forEach((entry, itemId) => {
      if (entry.isSelected) {
        selected.add(itemId);
      }
    });
    return selected;
  }, [isRootManaged, store]);

  const readItemMaps = useCallback(() => {
    const parentIdById = new Map<string, string>();
    const selectableIds = new Set<string>();
    entriesRef.current.forEach((entry, itemId) => {
      if (entry.parentId !== null) {
        parentIdById.set(itemId, entry.parentId);
      }
      if (entry.isSelectable && !entry.isDisabled) {
        selectableIds.add(itemId);
      }
    });
    return { parentIdById, selectableIds };
  }, []);

  const recomputeEdges = useCallback(() => {
    const root = treeRef.current;
    const previousEdges = store.getState().edgeById;
    if (!root || selectionMode !== 'multiple-replace') {
      if (previousEdges.size > 0) {
        store.setState({ edgeById: EMPTY_EDGES });
      }
      return;
    }
    const selectedIds = readSelectedIds();
    if (selectedIds.size === 0 && previousEdges.size === 0) {
      return;
    }
    const nextEdges = computeSelectionEdges(
      getVisibleItemIds(root),
      selectedIds,
    );
    if (!areEdgeMapsEqual(previousEdges, nextEdges)) {
      store.setState({ edgeById: nextEdges });
    }
  }, [readSelectedIds, selectionMode, store]);

  const recomputeEdgesRef = useRef(recomputeEdges);
  useLayoutEffect(() => {
    recomputeEdgesRef.current = recomputeEdges;
  });

  // Debounced through a microtask so a commit that touches many items (mount,
  // expansion, range selection) triggers a single recompute, after all item
  // layout effects have registered and before the browser paints.
  const isEdgeRecomputeScheduledRef = useRef(false);
  const scheduleEdgeRecompute = useCallback(() => {
    if (isEdgeRecomputeScheduledRef.current) {
      return;
    }
    isEdgeRecomputeScheduledRef.current = true;
    queueMicrotask(() => {
      isEdgeRecomputeScheduledRef.current = false;
      recomputeEdgesRef.current();
    });
  }, []);

  const registerItem = useCallback(
    (itemId: string, entry: ItemEntry | null) => {
      if (entry === null) {
        entriesRef.current.delete(itemId);
      } else {
        entriesRef.current.set(itemId, entry);
      }
      // Covers item mount/unmount and expansion toggles (a collapsing parent
      // re-registers because its toggleExpanded callback is recreated).
      scheduleEdgeRecompute();
    },
    [scheduleEdgeRecompute],
  );

  const onSelectionIntent = useCallback(
    (itemId: string, intent: SelectionIntent) => {
      const root = treeRef.current;
      const entry = entriesRef.current.get(itemId);
      if (!root || !entry || entry.isDisabled) {
        return;
      }
      if (!entry.isSelectable) {
        entry.toggleExpanded();
        return;
      }

      const { parentIdById, selectableIds } = readItemMaps();
      const selectedIds = readSelectedIds();
      const result = computeNextSelection({
        anchor: anchorRef.current,
        intent,
        parentIdById,
        selectableIds,
        selectedIds,
        selectionMode,
        targetId: itemId,
        visibleIds: getVisibleItemIds(root),
      });
      anchorRef.current = result.anchor;

      const nextSelectedIds = result.selectedIds;
      const changedCount = countChangedIds(selectedIds, nextSelectedIds);

      if (isRootManaged) {
        if (changedCount > 0) {
          setSelectedIdListRef.current([...nextSelectedIds]);
        }
      } else if (selectionMode === 'single') {
        // Single mode leaves clearing the previously selected item to the
        // consumer, so only the clicked item is told about the change.
        if (!selectedIds.has(itemId)) {
          entry.onSelectedChange?.(true);
        }
      } else {
        entriesRef.current.forEach((itemEntry, id) => {
          const isSelected = nextSelectedIds.has(id);
          if (itemEntry.isSelected !== isSelected) {
            itemEntry.onSelectedChange?.(isSelected);
          }
        });
      }

      scheduleEdgeRecompute();

      if (selectionMode !== 'single') {
        setAnnouncement(
          intent.isRange
            ? formatSelectionAnnouncement(nextSelectedIds.size)
            : '',
        );
      }
    },
    [
      isRootManaged,
      readItemMaps,
      readSelectedIds,
      scheduleEdgeRecompute,
      selectionMode,
    ],
  );

  // Mirrors the root-managed selection into the store, so items subscribe to
  // their own slice and event handlers read it without a state dependency.
  useLayoutEffect(() => {
    const next = isRootManaged ? new Set(selectedIdList) : EMPTY_IDS;
    if (areIdSetsEqual(store.getState().selectedIds, next)) {
      return;
    }
    store.setState({ selectedIds: next });
    scheduleEdgeRecompute();
  }, [isRootManaged, scheduleEdgeRecompute, selectedIdList, store]);

  // Computes edges on mount and whenever the edge inputs change identity
  // (selection mode, root-managed flag). Interaction-driven recomputes go
  // through scheduleEdgeRecompute instead.
  useLayoutEffect(() => {
    recomputeEdges();
  }, [recomputeEdges]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const root = treeRef.current;
      if (!root) {
        return;
      }

      const items = getVisibleTreeItems(root);
      if (items.length === 0) {
        return;
      }

      const currentIndex = items.findIndex(
        (el) => el.id === store.getState().focusedId,
      );
      let isHandled = true;

      const extendRangeTo = (item: HTMLElement) => {
        if (selectionMode === 'single' || currentIndex < 0) {
          return;
        }
        const entry = entriesRef.current.get(item.id);
        if (!entry?.isSelectable || entry.isDisabled) {
          return;
        }
        if (anchorRef.current === null) {
          // A keyboard range has no preceding click to repeat, so it grows the
          // selection outwards from wherever focus already is.
          anchorRef.current = {
            baseSelectedIds: readSelectedIds(),
            id: items[currentIndex].id,
            isSelected: true,
          };
        }
        onSelectionIntent(item.id, { isRange: true, isToggle: false });
      };

      switch (event.key) {
        case 'ArrowDown': {
          const nextIndex =
            currentIndex < items.length - 1 ? currentIndex + 1 : currentIndex;
          const nextItem = items[nextIndex];
          if (nextItem) {
            if (event.shiftKey) {
              extendRangeTo(nextItem);
            }
            setFocusedId(nextItem.id);
            nextItem.focus();
          }
          break;
        }
        case 'ArrowUp': {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
          const prevItem = items[prevIndex];
          if (prevItem) {
            if (event.shiftKey) {
              extendRangeTo(prevItem);
            }
            setFocusedId(prevItem.id);
            prevItem.focus();
          }
          break;
        }
        case 'ArrowRight': {
          const current = currentIndex >= 0 ? items[currentIndex] : null;
          if (!current) {
            break;
          }
          const isExpanded = current.getAttribute('aria-expanded');
          if (isExpanded === 'false') {
            current.dispatchEvent(new CustomEvent(TREE_EVENTS.expand));
          } else if (isExpanded === 'true') {
            const nextIndex = currentIndex + 1;
            if (nextIndex < items.length) {
              const nextItem = items[nextIndex];
              setFocusedId(nextItem.id);
              nextItem.focus();
            }
          }
          break;
        }
        case 'ArrowLeft': {
          const current = currentIndex >= 0 ? items[currentIndex] : null;
          if (!current) {
            break;
          }
          const isExpanded = current.getAttribute('aria-expanded');
          if (isExpanded === 'true') {
            current.dispatchEvent(new CustomEvent(TREE_EVENTS.collapse));
          } else {
            const parentGroup = current.closest('[role="group"]');
            if (parentGroup?.id) {
              const parentItem = root.querySelector<HTMLElement>(
                `[aria-owns="${parentGroup.id}"]`,
              );
              if (parentItem) {
                setFocusedId(parentItem.id);
                parentItem.focus();
              }
            }
          }
          break;
        }
        case 'Home': {
          const first = items[0];
          if (first) {
            setFocusedId(first.id);
            first.focus();
          }
          break;
        }
        case 'End': {
          const last = items[items.length - 1];
          if (last) {
            setFocusedId(last.id);
            last.focus();
          }
          break;
        }
        case 'Enter':
        case ' ': {
          const current = currentIndex >= 0 ? items[currentIndex] : null;
          if (current) {
            onSelectionIntent(current.id, getSelectionIntent(event));
          }
          break;
        }
        case 'F10': {
          if (event.shiftKey) {
            const current = currentIndex >= 0 ? items[currentIndex] : null;
            if (current) {
              current.dispatchEvent(new CustomEvent(TREE_EVENTS.action));
            }
          } else {
            isHandled = false;
          }
          break;
        }
        default:
          isHandled = false;
      }

      if (isHandled) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [onSelectionIntent, readSelectedIds, selectionMode, setFocusedId, store],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const root = treeRef.current;
      if (!root) {
        return;
      }

      setIsFocusWithin(true);

      if (event.target === root) {
        const items = getVisibleTreeItems(root);
        const { focusedId } = store.getState();
        const alreadyFocused = focusedId
          ? items.find((el) => el.id === focusedId)
          : null;
        const target = alreadyFocused ?? items[0];
        if (target) {
          setFocusedId(target.id);
          target.focus();
        }
      }
    },
    [setFocusedId, store],
  );

  const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const root = treeRef.current;
    if (!root) {
      return;
    }
    if (
      !(event.relatedTarget instanceof Node) ||
      !root.contains(event.relatedTarget)
    ) {
      setIsFocusWithin(false);
    }
  }, []);

  const classes = classNames(className, 'ndl-tree-view-new', {
    'ndl-tree-view-new--checkbox': selectionMode === 'multiple',
  });

  // Cached on children identity so Root-internal state commits (announcement,
  // uncontrolled selection) reuse the same annotated elements and React bails
  // out of the whole item subtree instead of re-rendering every row.
  const annotatedChildren = useMemo(
    () => annotateTreeItems(children),
    [children],
  );

  // Everything in here is stable or primitive, so items only re-render via
  // context when the tree's configuration changes, not per interaction.
  const contextValue = useMemo(
    () => ({
      isRootManaged,
      level: 1,
      onSelectionIntent,
      parentId: null,
      registerItem,
      selectionMode,
      setFocusedId,
      store,
    }),
    [
      isRootManaged,
      onSelectionIntent,
      registerItem,
      selectionMode,
      setFocusedId,
      store,
    ],
  );

  return (
    <TreeViewContext.Provider value={contextValue}>
      <div
        ref={mergedRef}
        role="tree"
        aria-multiselectable={
          selectionMode === 'multiple' || selectionMode === 'multiple-replace'
            ? true
            : undefined
        }
        tabIndex={isFocusWithin ? -1 : 0}
        className={classes}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={ariaLabel ?? undefined}
        aria-labelledby={ariaLabelledby}
        style={style}
        {...htmlAttributes}
        {...restProps}
      >
        {annotatedChildren}
      </div>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="ndl-tree-view-new-live-region"
      >
        {announcement}
      </div>
    </TreeViewContext.Provider>
  );
};

type ItemProps = {
  /** Nested TreeView.Item or TreeView.SkeletonItem elements rendered as sub-items in a collapsible group. */
  children?: React.ReactNode;
  /** The label content displayed in the item row. */
  title?: React.ReactNode;
  /** Default expansion for uncontrolled items */
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  defaultExpanded?: boolean;
  /** Whether the item has children. Decides if the item should render a chevron for expansion. Needed for lazy loading. */
  hasChildren?: boolean;
  /** Identifies the item. Required when the tree manages selection via `selectedIds`. */
  id?: string;
  /** Whether the item is disabled */
  isDisabled?: boolean;
  /** Whether the item is expanded. Makes the item controlled. */
  isExpanded?: boolean;
  /** Whether the item is in an indeterminate state. Only meaningful in multi-select mode for parent nodes. */
  isIndeterminate?: boolean;
  /** Whether the item is loading. Applies aria-busy="true" to the item. */
  isLoading?: boolean;
  /** Whether the item can be selected. Defaults to true when the tree manages selection, and otherwise to whether `onSelectedChange` is set. */
  isSelectable?: boolean;
  /** Whether the item is selected. Ignored when the tree manages selection via `selectedIds`. */
  isSelected?: boolean;
  /** Leading visual for the item. */
  leadingVisual?: React.ReactNode;
  /** Trailing content for the item. Shown after the children but before the action menu. */
  trailingContent?: React.ReactNode;
  /** ReactNode containing Menu.Item elements to render in an action menu. When provided, an action button is rendered. */
  actionMenuItems?: React.ReactNode;
  /** Props forwarded to the Menu sub-components (anchorRef, onClose). */
  actionMenuProps?: React.ComponentProps<typeof Menu>;
  /** Content rendered inside the tooltip. When provided, the item is wrapped in a Tooltip. */
  tooltipContent?: React.ReactNode;
  /** Props forwarded to the Tooltip sub-components (root, trigger, content). */
  tooltipProps?: TooltipObjectProps;
  /** Callback called when the item is expanded/collapsed. */
  onExpandedChange?: (isExpanded: boolean) => void;
  /** Callback called when the item is selected/deselected. Receives the new selected state. Ignored when the tree manages selection via `selectedIds`. */
  onSelectedChange?: (isSelected: boolean) => void;
};

/**
 * A single item within a `TreeView`. Renders a `role="treeitem"` element that
 * supports selection, expansion, leading/trailing visuals, and an action menu.
 *
 * The `title` prop provides the item's label content. Nesting `TreeView.Item`
 * elements as `children` creates a hierarchical tree structure.
 *
 * Supports both controlled (`isExpanded`) and uncontrolled (`defaultExpanded`)
 * expansion, as well as lazy loading via `hasChildren` + `isLoading`.
 *
 * @example
 * ```tsx
 * <TreeView.Item
 *   title="src"
 *   leadingVisual={<FolderIcon />}
 *   isSelected={selected}
 *   onSelectedChange={setSelected}
 * >
 *   <TreeView.Item title="index.ts" />
 * </TreeView.Item>
 * ```
 */
const Item = ({
  actionMenuItems,
  children,
  defaultExpanded = false,
  hasChildren: hasChildrenProp,
  id: idProp,
  isDisabled,
  isExpanded: isExpandedProp,
  isIndeterminate,
  isLoading,
  isSelectable: isSelectableProp,
  isSelected: isSelectedProp,
  leadingVisual,
  title,
  tooltipContent,
  tooltipProps,
  trailingContent,
  onExpandedChange,
  onSelectedChange,
  actionMenuProps,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', ItemProps>) => {
  const generatedId = useId();
  const itemId = idProp ?? htmlAttributes?.id ?? `ndl-treeitem-${generatedId}`;
  const titleId = `ndl-treeitem-title-${generatedId}`;
  const groupId = `ndl-treegroup-${generatedId}`;
  const itemRef = useRef<HTMLDivElement>(null);
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const didOpenViaKeyboard = useRef(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const treeViewContext = useContext(TreeViewContext);
  const {
    isRootManaged,
    level,
    onSelectionIntent,
    parentId,
    registerItem,
    selectionMode,
    setFocusedId,
    store,
  } = treeViewContext;

  // Primitive slices of the store, so Object.is bails out of re-renders for
  // rows whose focus/selection/edge state did not change.
  const isFocused = useSyncExternalStore(
    store.subscribe,
    () => store.getState().focusedId === itemId,
    () => store.getState().focusedId === itemId,
  );
  const isRootSelected = useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedIds.has(itemId),
    () => store.getState().selectedIds.has(itemId),
  );
  const edge = useSyncExternalStore(
    store.subscribe,
    () => store.getState().edgeById.get(itemId),
    () => store.getState().edgeById.get(itemId),
  );

  const isControlledExpansion = isExpandedProp !== undefined;
  const [isExpanded, setIsExpanded] = useSemicontrolledState({
    isControlled: isControlledExpansion,
    onChange: onExpandedChange,
    state: isExpandedProp ?? defaultExpanded,
  });

  const hasChildren = hasChildrenProp ?? Children.count(children) > 0;
  const isSelectable = isRootManaged
    ? (isSelectableProp ?? true)
    : (isSelectableProp ?? onSelectedChange !== undefined);
  const isSelected = isRootManaged ? isRootSelected : (isSelectedProp ?? false);
  const isMultiple = selectionMode === 'multiple';

  const toggleExpanded = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  }, [isDisabled, hasChildren, setIsExpanded, isExpanded]);

  useLayoutEffect(() => {
    registerItem(itemId, {
      isDisabled: isDisabled ?? false,
      isSelectable,
      isSelected,
      onSelectedChange,
      parentId,
      toggleExpanded,
    });
  }, [
    isDisabled,
    isSelectable,
    isSelected,
    itemId,
    onSelectedChange,
    parentId,
    registerItem,
    toggleExpanded,
  ]);

  useLayoutEffect(() => {
    return () => registerItem(itemId, null);
  }, [itemId, registerItem]);

  useEffect(() => {
    if (!isRootManaged) {
      return;
    }
    if (idProp === undefined) {
      needleWarningMessage(
        'TreeView.Item needs an `id` when the tree manages selection with `selectedIds`.',
      );
    }
    if (isSelectedProp !== undefined || onSelectedChange !== undefined) {
      needleWarningMessage(
        'TreeView.Item `isSelected` and `onSelectedChange` are ignored when the tree manages selection with `selectedIds`.',
      );
    }
  }, [idProp, isRootManaged, isSelectedProp, onSelectedChange]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (isDisabled) {
        return;
      }
      setFocusedId(itemId);
      onSelectionIntent(itemId, getSelectionIntent(event));
    },
    [isDisabled, setFocusedId, itemId, onSelectionIntent],
  );

  const handleChevronClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isDisabled) {
        return;
      }
      setFocusedId(itemId);
      toggleExpanded();
    },
    [isDisabled, setFocusedId, itemId, toggleExpanded],
  );

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    if (didOpenViaKeyboard.current) {
      itemRef.current?.focus();
      didOpenViaKeyboard.current = false;
    }
  }, []);

  const handleCustomEvents = useCallback(
    (event: Event) => {
      if (event.type === TREE_EVENTS.expand) {
        event.stopPropagation();
        if (hasChildren && !isExpanded && !isDisabled) {
          setIsExpanded(true);
        }
      } else if (event.type === TREE_EVENTS.collapse) {
        event.stopPropagation();
        if (hasChildren && isExpanded && !isDisabled) {
          setIsExpanded(false);
        }
      } else if (event.type === TREE_EVENTS.action) {
        event.stopPropagation();
        if (actionMenuItems && !isDisabled) {
          didOpenViaKeyboard.current = true;
          setIsMenuOpen(true);
        }
      }
    },
    [actionMenuItems, hasChildren, isExpanded, isDisabled, setIsExpanded],
  );

  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      const prev = itemRef.current;
      if (prev) {
        prev.removeEventListener(TREE_EVENTS.expand, handleCustomEvents);
        prev.removeEventListener(TREE_EVENTS.collapse, handleCustomEvents);
        prev.removeEventListener(TREE_EVENTS.action, handleCustomEvents);
      }
      (itemRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (node) {
        node.addEventListener(TREE_EVENTS.expand, handleCustomEvents);
        node.addEventListener(TREE_EVENTS.collapse, handleCustomEvents);
        node.addEventListener(TREE_EVENTS.action, handleCustomEvents);
      }
    },
    [handleCustomEvents],
  );

  const mergedRef = useMergeRefs([callbackRef, ref]);

  const selectionAriaProps = (() => {
    if (!isSelectable) {
      return {};
    }
    if (isMultiple) {
      return {
        'aria-checked': isIndeterminate ? ('mixed' as const) : isSelected,
      };
    }
    return { 'aria-selected': isSelected };
  })();

  const edgeClass =
    edge === undefined ? undefined : `ndl-tree-view-new-item--selected-${edge}`;

  const childContextValue = useMemo(
    () => ({
      ...treeViewContext,
      level: treeViewContext.level + 1,
      parentId: itemId,
    }),
    [itemId, treeViewContext],
  );

  // Same caching as in Root: keeps child element identity stable across
  // renders of this item (selection, focus, expansion) so unchanged
  // descendants bail out of re-rendering.
  const annotatedChildren = useMemo(
    () => annotateTreeItems(children),
    [children],
  );

  const itemElement = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard handled on tree root; aria-selected/aria-checked applied via spread
    <div
      ref={mergedRef}
      // eslint-disable-next-line jsx-a11y/role-has-required-aria-props -- aria-selected/aria-checked applied conditionally via spread
      role="treeitem"
      {...selectionAriaProps}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-owns={hasChildren ? groupId : undefined}
      aria-disabled={isDisabled || undefined}
      aria-busy={isLoading || undefined}
      aria-keyshortcuts={actionMenuItems ? 'Shift+F10' : undefined}
      aria-labelledby={titleId}
      aria-level={level}
      tabIndex={isFocused ? 0 : -1}
      className={classNames('ndl-tree-view-new-item', className, edgeClass, {
        'ndl-tree-view-new-item--disabled': isDisabled,
        'ndl-tree-view-new-item--parent': hasChildren,
        'ndl-tree-view-new-item--selected': isSelected && isSelectable,
      })}
      style={{ '--tree-level': level - 1, ...style } as React.CSSProperties}
      onClick={(event) => {
        event.stopPropagation();
        handleClick(event);
      }}
      {...htmlAttributes}
      {...restProps}
      id={itemId}
    >
      <div
        className={classNames('ndl-tree-view-new-item-content', {
          'ndl-tree-view-new-item-content--parent': hasChildren,
        })}
      >
        {hasChildren && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard handled on tree root
          <span
            className="ndl-tree-view-new-chevron"
            aria-disabled={isDisabled}
            onClick={handleChevronClick}
          >
            <ChevronRightIconOutline
              className={classNames('ndl-tree-view-new-chevron-icon', {
                'ndl-tree-view-new-chevron-icon--open': isExpanded,
              })}
            />
          </span>
        )}
        {leadingVisual && (
          <div className="ndl-tree-view-new-item-content-leading-visual">
            {leadingVisual}
          </div>
        )}
        {isMultiple && isSelectable && (
          <div className="ndl-tree-view-new-checkbox" inert>
            <Checkbox
              style={{ borderWidth: '1.5px' }}
              ariaLabel="Select"
              isChecked={isSelected}
              isIndeterminate={isIndeterminate}
              isDisabled={isDisabled}
              onChange={noop}
            />
          </div>
        )}
        <div id={titleId} className="ndl-tree-view-new-item-content-title">
          {title}
        </div>
        {trailingContent && (
          <div className="ndl-tree-view-new-item-content-trailing-content">
            {trailingContent}
          </div>
        )}
        {actionMenuItems && (
          <div
            className={classNames('ndl-tree-view-new-action', {
              'ndl-tree-view-new-action--visible': isMenuOpen,
            })}
          >
            <CleanIconButton
              ref={actionButtonRef}
              size="small"
              description={null}
              isDisabled={isDisabled}
              style={{ height: '24px', width: '24px' }}
              htmlAttributes={{
                'aria-expanded': isMenuOpen || undefined,
                'aria-haspopup': 'menu',
                'aria-label': 'Actions',
                onClick: (event: React.MouseEvent) => {
                  event.stopPropagation();
                  didOpenViaKeyboard.current = false;
                  setIsMenuOpen((prev) => !prev);
                },
                tabIndex: -1,
              }}
            >
              <EllipsisHorizontalIconOutline />
            </CleanIconButton>
            <Menu
              isOpen={isMenuOpen}
              anchorRef={actionButtonRef}
              onClose={handleMenuClose}
              {...actionMenuProps}
            >
              {actionMenuItems}
            </Menu>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ConditionalWrap
        shouldWrap={tooltipContent !== undefined && tooltipContent !== null}
        wrap={(children) => (
          <Tooltip
            type="simple"
            hoverDelay={{ close: 0, open: 500 }}
            isDisabled={isMenuOpen}
            followCursor="initial"
            {...tooltipProps?.root}
          >
            <Tooltip.Trigger hasButtonWrapper {...tooltipProps?.trigger}>
              {children}
            </Tooltip.Trigger>
            <Tooltip.Content {...tooltipProps?.content}>
              {tooltipContent}
            </Tooltip.Content>
          </Tooltip>
        )}
      >
        {itemElement}
      </ConditionalWrap>
      {hasChildren && (
        <TreeViewContext.Provider value={childContextValue}>
          <div
            role="group"
            id={groupId}
            hidden={!isExpanded}
            style={{ '--tree-level': level - 1 } as React.CSSProperties}
          >
            {annotatedChildren}
          </div>
        </TreeViewContext.Provider>
      )}
    </>
  );
};

type SkeletonItemProps = {
  /** The number of rows to render. */
  rows?: number;
};

/**
 * A skeleton placeholder for tree items that are being loaded. Renders one or
 * more `Skeleton` rows at the current nesting depth. The element is
 * `aria-hidden` since it is purely a visual indicator and not interactive.
 *
 * Typically used inside a `TreeView.Item` that has `isLoading` set, to give
 * the user a visual hint that child items are being fetched.
 *
 * @example
 * ```tsx
 * <TreeView.Item title="Lazy folder" hasChildren isLoading isExpanded>
 *   {isLoading && <TreeView.SkeletonItem rows={3} />}
 * </TreeView.Item>
 * ```
 */
const SkeletonItem = ({
  rows = 1,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', SkeletonItemProps>) => {
  const { level } = useContext(TreeViewContext);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={classNames('ndl-tree-view-new-skeleton', className)}
      style={{ '--tree-level': level - 1, ...style } as React.CSSProperties}
      {...htmlAttributes}
      {...restProps}
    >
      {Array.from({ length: rows }, (_unused, index) => (
        <Skeleton key={index} height="18px" width="100%" />
      ))}
    </div>
  );
};

/**
 * @remarks
 * Selection can be managed per item, with `isSelected` and `onSelectedChange`
 * on each `TreeView.Item`, or by the tree itself, by passing `selectedIds` (or
 * `defaultSelectedIds`) together with `onSelectedIdsChange`. When the tree
 * manages selection, every item needs an `id`, and each item's own
 * `isSelected` and `onSelectedChange` are ignored.
 *
 * The available selection modes are:
 *
 * - `single` — one item at a time. An unmodified click selects the clicked item and never clears it.
 * - `multiple` — multi-select with checkboxes. An unmodified click toggles an item, and `Shift` repeats that across a range without clearing anything outside it.
 * - `multiple-replace` — multi-select without checkboxes. An unmodified click selects only that item, `Cmd`/`Ctrl` toggles it and moves the anchor, and `Shift` extends from that anchor while retaining its selection snapshot. `Shift` + arrow extends the range.
 */
const TreeView = Object.assign(Root, { Item, SkeletonItem });
export { TreeView };
